import { motion } from 'framer-motion';
import { useAuth } from '@/hooks/use-auth';
import { User, Video, Upload, Calendar, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CurriculumTile } from '@/components/v2/tiles/CurriculumTile';
import { CoachFeedbackTile } from '@/components/v2/tiles/CoachFeedbackTile';
import { ClubhouseActivityTile } from '@/components/v2/tiles/ClubhouseActivityTile';
import { EverestProgressTile } from '@/components/v2/tiles/EverestProgressTile';
import { QuickActionsTile } from '@/components/v2/tiles/QuickActionsTile';
import { SuggestionsPanel } from '@/features/onboarding/components/SuggestionsPanel';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { FirstTimeUserGuide } from '@/components/onboarding/FirstTimeUserGuide';
import { useFirstTimeUserGuide } from '@/hooks/useFirstTimeUserGuide';

/**
 * Home Dashboard V2 - FIGMA Clubhouse 2.0 Design
 * 
 * This is the new home dashboard with tiles for:
 * - Curriculum (active drills)
 * - Coach Feedback (recent analysis)
 * - Clubhouse Activity (teams/challenges preview)
 * - Everest Progress (personal goals)
 * - Quick Actions (common tasks)
 * 
 * @TODO Wire curriculum to drills_curriculum table
 * @TODO Wire feedback to swing_analysis_runs table
 * @TODO Wire clubhouse to teams/challenges tables
 * @TODO Wire everest to user_goals table
 */
export default function HomeV2() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3, ease: 'easeOut' },
    },
  };

  // Get user's first name or fallback
  const firstName = user?.user_metadata?.full_name?.split(' ')[0] || 'Golfer';

  // Fetch user profile for suggestions
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('region, skill_level, onboarded, zip_code')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // First-time user guide
  const { shouldShow: showGuide, dismiss: dismissGuide } = useFirstTimeUserGuide();

  // Handler functions (will be wired to actual navigation)
  const handleDrillClick = (drill: any) => {
    console.log('Drill clicked:', drill);
    navigate('/practice');
  };

  const handleViewAllDrills = () => {
    navigate('/practice');
  };

  const handleFeedbackClick = (item: any) => {
    console.log('Feedback clicked:', item);
    navigate('/videos');
  };

  const handleViewAllFeedback = () => {
    navigate('/videos');
  };

  const handleTeamClick = (team: any) => {
    console.log('Team clicked:', team);
    navigate('/clubhouse');
  };

  const handleChallengeClick = (challenge: any) => {
    console.log('Challenge clicked:', challenge);
    navigate('/clubhouse');
  };

  const handleEverestDetails = () => {
    navigate('/clubhouse');
  };

  return (
    <div className="min-h-screen pb-20">
      {/* Header - FIGMA gradient */}
      <header className="bg-gradient-to-r from-emerald-700 to-emerald-600 px-4 md:px-8 py-8 md:py-12 shadow-lg">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-7xl mx-auto"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1" />
            <div className="flex-1 flex justify-center">
              <img
                src="/lovable-uploads/bbc133b7-6cd1-44ba-941b-2340e4755a16.png"
                alt="No Doubles Golf Co."
                className="h-12"
              />
            </div>
            <div className="flex-1 flex justify-end">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/profile')}
                className="text-white hover:bg-white/10"
              >
                <User size={20} />
              </Button>
            </div>
          </div>
          
          <div className="text-center">
            <h1 className="text-white mb-2 text-3xl md:text-4xl">Welcome back, {firstName}</h1>
            <p className="text-emerald-100">
              Golf is hard. Getting better shouldn't be.
            </p>
          </div>
        </motion.div>
      </header>

      {/* Dashboard Grid */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="px-4 md:px-8 py-6 md:py-8 max-w-7xl mx-auto"
      >
        {/* Primary Tiles - Large tiles in 2-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <motion.div variants={itemVariants}>
            <CurriculumTile
              onDrillClick={handleDrillClick}
              onViewAll={handleViewAllDrills}
            />
          </motion.div>
          <motion.div variants={itemVariants}>
            <CoachFeedbackTile
              onFeedbackClick={handleFeedbackClick}
              onViewAll={handleViewAllFeedback}
            />
          </motion.div>
        </div>

        {/* Secondary Tiles - 3-column grid on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <motion.div variants={itemVariants}>
            <ClubhouseActivityTile
              onTeamClick={handleTeamClick}
              onChallengeClick={handleChallengeClick}
            />
          </motion.div>
          <motion.div variants={itemVariants}>
            <EverestProgressTile onViewDetails={handleEverestDetails} />
          </motion.div>
          <motion.div variants={itemVariants}>
            {/* PLA-143: Quick actions use V3 upload flow */}
            <QuickActionsTile
              actions={[
                {
                  icon: Video,
                  label: 'Record Swing',
                  color: 'from-red-500 to-red-600',
                  onClick: () => navigate('/mvp/upload'),
                },
                {
                  icon: Upload,
                  label: 'Upload Video',
                  color: 'from-blue-500 to-blue-600',
                  onClick: () => navigate('/mvp/upload'),
                },
                {
                  icon: Calendar,
                  label: 'View Progress',
                  color: 'from-purple-500 to-purple-600',
                  onClick: () => navigate('/dashboard'),
                },
                {
                  icon: MessageCircle,
                  label: 'Clubhouse',
                  color: 'from-emerald-500 to-emerald-600',
                  onClick: () => navigate('/clubhouse'),
                },
              ]}
            />
          </motion.div>
        </div>

        {/* Suggestions Panel - Show after onboarding */}
        {profile?.onboarded && user && (
          <motion.div variants={itemVariants} className="mt-6">
            <SuggestionsPanel
              userId={user.id}
              region={profile.region || undefined}
              zipCode={profile.zip_code || undefined}
              radius={25} // Default radius
              country={profile.region?.startsWith('US -') ? 'US' : profile.region?.startsWith('CA -') ? 'CA' : undefined}
              skillLevel={profile.skill_level || undefined}
            />
          </motion.div>
        )}
      </motion.div>

      {/* First-Time User Guide Overlay */}
      {showGuide && user && (
        <FirstTimeUserGuide onDismiss={dismissGuide} />
      )}
    </div>
  );
}

